import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import HttpStatus from 'http-status-codes';
import Cookies from 'js-cookie';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import i18n from './i18n';
import config from './config';
import getStore from './redux/store';
import theme from './theme';
import { steps } from './helpers/const';
import api from './helpers/api';
import Utils from './helpers/utils';

const ECommerceFrontend = React.lazy(() => import(/* webpackChunkName: "EcommerceFrontEnd" */ './sections/ECommerceFrontend'));
const QuotationFrontend = React.lazy(() => import(/* webpackChunkName: "QuotationFrontEnd" */ './sections/QuotationFrontend'));
const VehicleSearch = React.lazy(() => import(/* webpackChunkName: "VehicleSearch" */ './sections/VehicleSearch'));
const ecommerceNode = document.getElementById('ecommerce-frontend');
const quotationNode = document.getElementById('quotation-frontend');
const vehicleSearchNode = document.getElementById('vehicle-search');

const getECommerce = () => (
  <Suspense fallback={<h1 className="ga-ecommerce-fallback">...</h1>}>
    <ECommerceFrontend />
  </Suspense>
);
const getQuotation = () => (
  <Suspense fallback={<h1 className="ga-quotation-fallback">...</h1>}>
    <QuotationFrontend />
  </Suspense>
);
const getVehicleSearch = () => (
  <Suspense fallback={<h1 className="ga-vehicle-search-fallback">...</h1>}>
    <VehicleSearch />
  </Suspense>
);
// Avoid render in case of inactive feature
if (typeof ecommerceNode !== 'undefined' && ecommerceNode !== null) {
  const currentLanguage = Cookies.get(config.langCookie);
  const ecommerceStoreInitial = {
    wizard: {
      loading: false,
      actionRequired: false,
      error: false,
      errorMessage: '',
      success: false,
      step: steps.personal_data,
      subStep: 0,
      validStep: false,
      validateForm: false,
    },
  };
  i18n.changeLanguage(currentLanguage);
  ReactDOM.createRoot(ecommerceNode).render(
    <Provider store={getStore(ecommerceStoreInitial)}>
      <ThemeProvider theme={theme}>
        { getECommerce() }
      </ThemeProvider>
    </Provider>,
  );
}

if (typeof quotationNode !== 'undefined' && quotationNode !== null) {
  const getModelsLookup = async (make) => {
    const res = await api.get({
      entryPoint: `/infocar/makes/${make}/models`,
      silentFail: false,
      pgp: true,
      auth: false,
    });
    if (res?.status === HttpStatus.OK) {
      return res.data.result.values;
    }
    return [];
  };
  const currentLanguage = Cookies.get(config.langCookie);
  i18n.changeLanguage(currentLanguage);
  const quotationStoreInitial = {
    wizard: {
      loading: false,
      actionRequired: false,
      error: false,
      errorMessage: '',
      success: false,
      step: steps.trade,
      subStep: 0,
      validStep: false,
      validateForm: false,
    },
    lead: {
      leadId: 0,
      personalData: {
        tradeIn: true,
        financing: true,
        receiveOffer: false,
      },
      selectedServices: [],
      files: [],
      servicesTotal: 0,
      financialData: {},
      quote: {
        files: [],
      },
      cookie: {},
      quoteSteps: [
        { id: 'makemodel', isValid: false },
        { id: 'tech', isValid: false },
        { id: 'data', isValid: false },
        { id: 'resume', isValid: true },
      ],
    },
    lookups: {
      attachmentTypes: [],
      quote: {
        makes: [],
        models: [],
        versions: [],
        versionsLoad: false,
      },
      configuration: {},
    },
  };
  // Get values from pre-quotation
  const prequote = localStorage.getItem('prequote');
  if (prequote) {
    const pData = JSON.parse(prequote);
    const models = getModelsLookup(pData.make);
    models.then((mo) => {
      quotationStoreInitial.lookups.quote.models = Utils.sortModels(mo);
      quotationStoreInitial.lead.quote.makeId = pData.make;
      quotationStoreInitial.lead.quote.modelId = pData.model;
      quotationStoreInitial.lead.quote.makeText = pData.make_text;
      quotationStoreInitial.lead.quote.stateRegistrationYear = pData.year;
      ReactDOM.createRoot(quotationNode).render(
        <Provider store={getStore(quotationStoreInitial)}>
          <ThemeProvider theme={theme}>
            { getQuotation() }
          </ThemeProvider>
        </Provider>,
      );
    });
  } else {
    ReactDOM.createRoot(quotationNode).render(
      <Provider store={getStore(quotationStoreInitial)}>
        <ThemeProvider theme={theme}>
          { getQuotation() }
        </ThemeProvider>
      </Provider>,
    );
  }
}

if (typeof vehicleSearchNode !== 'undefined' && vehicleSearchNode !== null) {
  const currentLanguage = Cookies.get(config.langCookie);
  i18n.changeLanguage(currentLanguage);
  ReactDOM.createRoot(vehicleSearchNode).render(
    <Provider store={getStore({})}>
      <ThemeProvider theme={theme}>
        { getVehicleSearch() }
      </ThemeProvider>
    </Provider>,
  );
}
